import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import SettingsModal from './SettingsModal';
import axios from 'axios';

const CompanySetupGuard = ({ children }) => {
  const { user } = useAuth();
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  useEffect(() => {
    if (user && user.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR')) {
      checkCompanySetup();
    } else {
      setLoading(false);
    }
  }, [user]);

  const checkCompanySetup = async () => {
    try {
      const response = await axios.get('/api/company');
      setCompanyInfo(response.data);
      
      // Check if essential company information is missing
      if (!response.data.companyName) {
        setShowSettingsModal(true);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // No company data exists, force setup
        setShowSettingsModal(true);
      } else {
        console.error('Failed to check company setup:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySetupComplete = () => {
    setShowSettingsModal(false);
    checkCompanySetup(); // Refresh company info
  };

  // Show loading while checking
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--gray-light)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <div style={{ fontSize: '18px', color: 'var(--dark-blue)' }}>
            Checking company setup...
          </div>
        </div>
      </div>
    );
  }

  // If user is not SYSTEM_EDITOR, allow access
  if (!user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR')) {
    return children;
  }

  // If company setup is required, show modal
  if (showSettingsModal) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--gray-light)',
        padding: '20px'
      }}>
        <div style={{
          background: 'var(--white)',
          borderRadius: '12px',
          padding: '40px',
          maxWidth: '600px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>🏢</div>
          
          <h1 style={{
            color: 'var(--dark-blue)',
            marginBottom: '20px',
            fontSize: '28px'
          }}>
            Company Setup Required
          </h1>

          <div style={{
            background: 'var(--pale-blue)',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '30px',
            border: '2px solid var(--primary-blue)'
          }}>
            <p style={{
              color: 'var(--dark-blue)',
              fontSize: '16px',
              marginBottom: '15px',
              fontWeight: '500'
            }}>
              Please complete your company information setup to continue using the platform.
            </p>
            
            <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
              <strong>Required information:</strong>
              <ul style={{ textAlign: 'left', marginTop: '10px', marginLeft: '20px' }}>
                <li>Company name</li>
                <li>Contact information</li>
                <li>Address details</li>
                <li>GST and FSSAI numbers (if applicable)</li>
              </ul>
            </div>
          </div>

          <SettingsModal 
            user={user} 
            onClose={handleCompanySetupComplete}
            forceCompanyTab={true}
          />
        </div>
      </div>
    );
  }

  // Company setup is complete, allow access
  return children;
};

export default CompanySetupGuard;
